(() => {
  const STORAGE_KEY = 'personliserQuoteDetails';
  const FILES_KEY = 'personliserQuoteFiles';
  const filledNotes = new WeakMap();
  let lastDetails = '';
  let syncTimer = 0;

  const style = document.createElement('style');
  style.textContent = `
    .personliser-qw-details {
      margin: 0.6rem 0 0;
      padding: 0.8rem 1rem;
      border: 1px solid rgba(18, 18, 18, 0.12);
      border-radius: 0.8rem;
      background: #f6f4f0;
      font-size: 1.2rem;
      line-height: 1.45;
      color: #121212;
    }
    .personliser-qw-details strong {
      display: block;
      margin: 0 0 0.4rem;
      font-size: 1.1rem;
    }
    .personliser-qw-details strong + strong {
      margin-top: 0.8rem;
    }
    .personliser-qw-details p {
      margin: 0;
      white-space: pre-wrap;
    }
    .personliser-qw-details a {
      display: inline-block;
      margin: 0.2rem 0 0;
      color: #121212;
      text-decoration: underline;
    }
    .personliser-qw-file {
      display: block;
      margin-top: 0.35rem;
    }
  `;
  document.head.appendChild(style);

  function setNativeValue(input, value) {
    const proto = input.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) setter.call(input, value);
    else input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isFileUrl(value) {
    const url = String(value || '').trim();
    if (!/^(https?:)?\/\//i.test(url) && !url.startsWith('/')) return false;
    return /\/uploads\/|cdn\.shopify\.com\/s\/files|\/cdn\/shop\//i.test(url);
  }

  function absoluteUrl(value) {
    const url = String(value || '');
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  function fileName(url) {
    try {
      return decodeURIComponent(String(url).split('/').pop().split('?')[0]) || 'Download';
    } catch (error) {
      return 'Download';
    }
  }

  function collectFiles(properties) {
    if (!properties) return [];
    return Object.entries(properties)
      .filter(([key, value]) => value && !String(key).startsWith('_') && isFileUrl(value))
      .map(([key, value]) => ({
        label: key,
        url: absoluteUrl(value),
        name: fileName(value),
      }))
      .filter((file) => file.url);
  }

  function parseFileLines(text) {
    return String(text || '')
      .split('\n')
      .map((line) => {
        const match = line.match(/^(.+?):\s+(https?:\/\/\S+|\/\S*uploads\/\S+)/i);
        if (!match || !isFileUrl(match[2])) return null;
        return { label: match[1].trim(), url: absoluteUrl(match[2]), name: fileName(match[2]) };
      })
      .filter(Boolean);
  }

  function storedUnitPrice() {
    const value = Number(sessionStorage.getItem('personliserQuoteUnitPrice'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  function storedLineTotal() {
    const value = Number(sessionStorage.getItem('personliserQuoteLineTotal'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  function storedQty() {
    const value = Number(sessionStorage.getItem('personliserQuoteQty'));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD',
      }).format(Number(amount) || 0);
    } catch (error) {
      return `$${(Number(amount) || 0).toFixed(2)}`;
    }
  }

  function variantKey(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function unitPriceFromCart(cart, variantId) {
    if (!cart?.items) return 0;
    const feeCents = (cart.items || [])
      .filter((item) => item.properties?.['_Personliser fee'] === 'true')
      .reduce((sum, item) => sum + Number(item.final_line_price || item.line_price || 0), 0);
    const garment = (cart.items || []).find((item) => {
      if (item.properties?.['_Personliser fee'] === 'true') return false;
      return !variantId || variantKey(item.variant_id) === variantKey(variantId);
    });
    if (!garment) return storedUnitPrice();
    const base = Number(garment.final_price || garment.price || 0);
    const propertyFee = Number(garment.properties?._personliser_fee_cents || 0);
    const qty = Number(garment.quantity || 1);
    const extra = propertyFee > 0 ? propertyFee : feeCents / qty;
    return (base + extra) / 100;
  }

  function patchDrawerPrices(unit, qty, currency) {
    if (!unit) return;
    const listed = formatMoney(unit, currency);
    const total = formatMoney(storedLineTotal() || unit * qty, currency);
    document.querySelectorAll('.qw-line-catalog').forEach((el) => {
      const label = (el.textContent.split(':')[0] || 'Listed').trim();
      el.textContent = `${label}: ${listed}`;
    });
    document.querySelectorAll('.qw-subtotal, .qw-review-total').forEach((el) => {
      if (/[\d]/.test(el.textContent)) el.textContent = total;
    });
    document.querySelectorAll('.qw-review-line-price, .qw-price').forEach((el) => {
      if (/[\d]/.test(el.textContent)) el.textContent = listed;
    });
  }

  function storedFiles() {
    try {
      const raw = sessionStorage.getItem(FILES_KEY);
      if (!raw) return [];
      return JSON.parse(raw).map((entry) => {
        if (typeof entry === 'string') {
          const [label, ...rest] = entry.split(': ');
          const url = rest.join(': ');
          return { label, url: absoluteUrl(url), name: fileName(url) };
        }
        return entry;
      }).filter((file) => file?.url);
    } catch (error) {
      return [];
    }
  }

  function formatProperties(properties) {
    if (!properties) return '';
    return Object.entries(properties)
      .filter(([key, value]) => {
        if (!value || value === 'No' || value === 'no') return false;
        if (String(key).startsWith('_')) return false;
        return true;
      })
      .map(([key, value]) => `${key}: ${isFileUrl(value) ? absoluteUrl(value) : value}`)
      .join('\n');
  }

  function liveDetails() {
    const el = document.querySelector('personliser-customizer');
    if (el && typeof el.quoteDetails === 'function') {
      const details = el.quoteDetails();
      if (details) {
        lastDetails = details;
        try {
          sessionStorage.setItem(STORAGE_KEY, details);
        } catch (error) {
          /* ignore */
        }
        return details;
      }
    }
    try {
      return sessionStorage.getItem(STORAGE_KEY) || lastDetails || '';
    } catch (error) {
      return lastDetails || '';
    }
  }

  async function cartLines() {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .catch(() => null);
    if (!cart) return { byVariant: {}, byTitle: {}, note: '', files: storedFiles() };
    const byVariant = {};
    const byTitle = {};
    const files = [];
    (cart.items || []).forEach((item) => {
      if (item.properties?._Personliser_fee === 'true' || item.properties?.['_Personliser fee'] === 'true') return;
      const text = item.properties?.['Customization Details'] || formatProperties(item.properties);
      const itemFiles = collectFiles(item.properties);
      files.push(...itemFiles);
      const payload = { text, files: itemFiles, unitPrice: 0 };
      byVariant[String(item.variant_id)] = payload;
      byTitle[String(item.variant_title || '').toLowerCase()] = payload;
      byTitle[String(item.product_title || '').toLowerCase()] = payload;
    });
    const cartRef = cart;
    Object.values(byVariant).forEach((payload) => {
      payload.unitPrice = unitPriceFromCart(cartRef);
    });
    return {
      byVariant,
      byTitle,
      note: cart.note || cart.attributes?.['Customization Details'] || '',
      files: files.length ? files : storedFiles(),
      cart,
      unitPrice: unitPriceFromCart(cart) || storedUnitPrice(),
    };
  }

  function detailsForLine(line, cart, fallback) {
    const variant = (line.querySelector('.qw-line-variant')?.textContent || '').trim().toLowerCase();
    const title = (line.querySelector('.qw-line-title')?.textContent || '').trim().toLowerCase();
    const match = cart.byTitle[variant] || cart.byTitle[title];
    const text = match?.text || fallback || cart.note || '';
    const files = match?.files?.length ? match.files : [...collectFilesFromText(text), ...cart.files];
    return { text, files: uniqueFiles(files) };
  }

  function collectFilesFromText(text) {
    return parseFileLines(text);
  }

  function uniqueFiles(files) {
    const seen = new Set();
    return (files || []).filter((file) => {
      const key = `${file.label}|${file.url}`;
      if (!file.url || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function noteText(text, files) {
    const extra = files.map((file) => `${file.label}: ${file.url}`);
    const combined = [text, extra.length ? 'Downloads:' : '', ...extra].filter(Boolean).join('\n');
    return combined;
  }

  function injectLine(line, payload) {
    const text = payload?.text || '';
    const files = payload?.files || [];
    if (!text && !files.length) return;
    const info = line.querySelector('.qw-line-info') || line;
    let box = line.querySelector('[data-personliser-qw-details]');
    if (!box) {
      box = document.createElement('div');
      box.className = 'personliser-qw-details';
      box.setAttribute('data-personliser-qw-details', 'true');
      const after = info.querySelector('.qw-line-variant') || info.querySelector('.qw-price');
      if (after) after.after(box);
      else info.appendChild(box);
    }
    const textHtml = text
      ? `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`
      : '';
    const fileHtml = files.length
      ? `<strong>Download mockups</strong>${files
          .map((file) => `<a class="personliser-qw-file" href="${escapeHtml(file.url)}" target="_blank" rel="noopener" download>${escapeHtml(file.label)}: ${escapeHtml(file.name)}</a>`)
          .join('')}`
      : '';
    box.innerHTML = `<strong>Customization details</strong>${textHtml}${fileHtml}`;

    const note = line.querySelector('.qw-line-note');
    if (!note) return;
    const next = noteText(text, files);
    const previous = filledNotes.get(note);
    const userEdited = previous && note.value !== previous && note.value.trim();
    if (userEdited) return;
    if (note.value.trim() === next) {
      filledNotes.set(note, next);
      return;
    }
    setNativeValue(note, next);
    filledNotes.set(note, next);
  }

  function fillFormNotes(text) {
    if (!text) return;
    document.querySelectorAll('textarea.qw-notes').forEach((input) => {
      const previous = filledNotes.get(input);
      const userEdited = previous && input.value !== previous && input.value.trim();
      if (userEdited || input.value.trim() === text) return;
      if (input.value.trim()) return;
      setNativeValue(input, text);
      filledNotes.set(input, text);
    });
  }

  const attachedInputs = new WeakSet();

  function attachDesignFiles(drawer) {
    const files = Array.isArray(window.personliserQuoteAttachments) ? window.personliserQuoteAttachments : [];
    if (!files.length || typeof DataTransfer === 'undefined') return;
    const inputs = Array.from(drawer.querySelectorAll('input[type="file"]')).filter(
      (input) => !attachedInputs.has(input) && !input.disabled
    );
    if (!inputs.length) return;
    const limit = Number(window.personliserQuoteAttachmentLimit || 3);
    let index = 0;
    inputs.forEach((input) => {
      if (index >= files.length || index >= limit) return;
      const transfer = new DataTransfer();
      const count = input.multiple ? Math.min(limit - index, files.length - index) : 1;
      files.slice(index, index + count).forEach((file) => transfer.items.add(file));
      index += count;
      try {
        input.files = transfer.files;
        attachedInputs.add(input);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        /* browser blocked programmatic files */
      }
    });
  }

  async function syncDrawer() {
    const drawer = document.querySelector('.qw-drawer');
    if (!drawer) return;
    attachDesignFiles(drawer);
    const lines = drawer.querySelectorAll('.qw-line');
    if (!lines.length) return;
    const fallback = liveDetails();
    const cart = await cartLines();
    const shared = fallback || cart.note;
    lines.forEach((line) => injectLine(line, detailsForLine(line, cart, shared)));
    fillFormNotes(noteText(shared, cart.files));
    const qtyInput = drawer.querySelector('.qw-qty input, input.qw-qty');
    const qty = Number(qtyInput?.value || storedQty() || 1);
    const currency = window.Shopify?.currency?.active || 'USD';
    patchDrawerPrices(cart.unitPrice || storedUnitPrice(), qty, currency);
  }

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncDrawer().catch(() => null);
    }, 80);
  }

  function dashboardNote() {
    const details = liveDetails();
    const files = storedFiles();
    return noteText(details, files);
  }

  function mergeNote(existing, extra) {
    if (!extra) return existing || '';
    if (existing && existing.includes(extra.slice(0, 48))) return existing;
    return [extra, existing].filter(Boolean).join('\n\n');
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(init.method || input?.method || 'GET').toUpperCase();
    const isCreate = /\/quotes\/?(\?|$)/i.test(url) && /quotway/i.test(url) && method === 'POST';
    if (!isCreate || /\/attachments/i.test(url) || !init.body || typeof init.body !== 'string') {
      return nativeFetch(input, init);
    }
    try {
      const payload = JSON.parse(init.body);
      const extra = dashboardNote();
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' })
        .then((response) => response.json())
        .catch(() => null);
      if (Array.isArray(payload.lineItems)) {
        payload.lineItems = payload.lineItems.map((line) => {
          const unit = unitPriceFromCart(cart, line.variantId) || storedUnitPrice() || line.catalogPrice;
          return {
            ...line,
            catalogPrice: unit,
            requestedPrice: unit,
            buyerNote: mergeNote(line.buyerNote, extra),
          };
        });
        if (extra) payload.notes = mergeNote(payload.notes, extra);
        init = { ...init, body: JSON.stringify(payload) };
      }
    } catch (error) {
      /* ignore and send original */
    }
    return nativeFetch(input, init);
  };

  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('click', (event) => {
    const target = event.target.closest('button, a, [role="button"]');
    if (!target) return;
    const text = `${target.textContent || ''} ${target.getAttribute('aria-label') || ''}`.toLowerCase();
    const cls = `${target.className || ''}`.toLowerCase();
    if (text.includes('request a quote') || cls.includes('quotway') || cls.includes('qw-button')) {
      liveDetails();
      window.setTimeout(scheduleSync, 200);
      window.setTimeout(scheduleSync, 700);
    }
  });
})();
